import { useEffect } from "react";

interface SEOHeadProps {
  title: string;
  description: string;
  keywords?: string;
  canonicalPath?: string;
  ogImage?: string;
  ogType?: "website" | "article";
  structuredData?: Record<string, unknown>[];
}

function setMetaTag(selector: string, content: string, attribute: string = "content"): void {
  let el = document.querySelector(selector) as HTMLMetaElement | HTMLLinkElement | null;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(selector.includes("property") ? "property" : "name", selector.replace(/^meta\[(?:property|name)="([^"]+)"\]$/, "$1"));
    document.head.appendChild(el);
  }
  el.setAttribute(attribute, content);
}

function removeMetaTag(selector: string): void {
  const el = document.querySelector(selector);
  if (el) el.remove();
}

export default function SEOHead({
  title,
  description,
  keywords,
  canonicalPath = "/",
  ogImage,
  ogType = "website",
  structuredData,
}: SEOHeadProps) {
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const canonicalUrl = `${origin}${canonicalPath}`;

  useEffect(() => {
    // Title
    document.title = title;

    // Basic meta
    setMetaTag('meta[name="description"]', description);
    if (keywords) {
      setMetaTag('meta[name="keywords"]', keywords);
    } else {
      removeMetaTag('meta[name="keywords"]');
    }

    // Canonical
    let canonEl = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonEl) {
      canonEl = document.createElement("link");
      canonEl.setAttribute("rel", "canonical");
      document.head.appendChild(canonEl);
    }
    canonEl.setAttribute("href", canonicalUrl);

    // Open Graph
    setMetaTag('meta[property="og:title"]', title);
    setMetaTag('meta[property="og:description"]', description);
    setMetaTag('meta[property="og:type"]', ogType);
    setMetaTag('meta[property="og:url"]', canonicalUrl);
    if (ogImage) {
      setMetaTag('meta[property="og:image"]', ogImage);
    } else {
      removeMetaTag('meta[property="og:image"]');
    }

    // Twitter Card
    setMetaTag('meta[name="twitter:card"]', "summary_large_image");
    setMetaTag('meta[name="twitter:title"]', title);
    setMetaTag('meta[name="twitter:description"]', description);
    if (ogImage) {
      setMetaTag('meta[name="twitter:image"]', ogImage);
    } else {
      removeMetaTag('meta[name="twitter:image"]');
    }

    // Structured Data (JSON-LD)
    let scriptEl = document.querySelector('script[data-seo="structured-data"]');
    if (structuredData && structuredData.length > 0) {
      if (!scriptEl) {
        scriptEl = document.createElement("script");
        scriptEl.setAttribute("type", "application/ld+json");
        scriptEl.setAttribute("data-seo", "structured-data");
        document.head.appendChild(scriptEl);
      }
      scriptEl.textContent = JSON.stringify(
        structuredData.length === 1
          ? { "@context": "https://schema.org", ...structuredData[0] }
          : { "@context": "https://schema.org", "@graph": structuredData },
      );
    } else if (scriptEl) {
      scriptEl.remove();
    }

    // Cleanup: remove all dynamic tags on unmount so stale data doesn't persist across page navigations
    return () => {
      removeMetaTag('meta[name="description"]');
      removeMetaTag('meta[name="keywords"]');
      removeMetaTag('meta[property="og:title"]');
      removeMetaTag('meta[property="og:description"]');
      removeMetaTag('meta[property="og:type"]');
      removeMetaTag('meta[property="og:url"]');
      removeMetaTag('meta[property="og:image"]');
      removeMetaTag('meta[name="twitter:card"]');
      removeMetaTag('meta[name="twitter:title"]');
      removeMetaTag('meta[name="twitter:description"]');
      removeMetaTag('meta[name="twitter:image"]');
      const sd = document.querySelector('script[data-seo="structured-data"]');
      if (sd) sd.remove();
    };
  }, [title, description, keywords, canonicalUrl, ogImage, ogType, structuredData]);

  // Return nothing visually — all meta tags are injected into <head> via DOM manipulation
  return null;
}